import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Box, Flex, HStack, VStack, Text, Badge, Button, IconButton, Input, Select,
    Spinner, Tooltip, Table, Thead, Tbody, Tr, Th, Td, Drawer, DrawerOverlay,
    DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton, Divider, Tag,
    TagLabel, Wrap, WrapItem, SimpleGrid, Checkbox, useBreakpointValue, useToast,
} from '@chakra-ui/react';
import {
    FaSyncAlt, FaSearch, FaMicrophone, FaFileAlt, FaChevronRight,
    FaChevronLeft, FaRegClock, FaPhoneAlt,
} from 'react-icons/fa';
import { historicoUra, detalheHistoricoUra, audioHistoricoUra } from '../../services/api';
import {
    ACCENT, DESFECHOS, desfechoDe, visualDoNo, rotuloSaida, ROTULO_CONDICAO,
    fmtSeg, fmtMs, fmtNumero, fmtDataHora, fmtHoraMin, diasAtras, hojeISO,
} from './uraUtils';

/** Calculado na hora: página aberta virando o dia não pode ficar com o período de ontem. */
const filtroInicial = () => ({
    data_inicio: diasAtras(29),
    data_fim: hojeISO(),
    desfecho: '',
    digito: '',
    no: '',
    q: '',
    com_gravacao: false,
});

/* ==================================================================
 * Filtros
 * ================================================================== */

const Filtros = ({ filtros, setFiltros, onBuscar, carregando }) => {
    const mudar = (campo) => (e) => {
        const valor = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFiltros((f) => ({ ...f, [campo]: valor }));
    };

    return (
        <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" p={{ base: 3, md: 4 }} mb={3}>
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3}>
                <Box>
                    <Text fontSize="xs" color="gray.500" mb={1}>De</Text>
                    <Input size="sm" type="date" value={filtros.data_inicio} onChange={mudar('data_inicio')} />
                </Box>
                <Box>
                    <Text fontSize="xs" color="gray.500" mb={1}>Até</Text>
                    <Input size="sm" type="date" value={filtros.data_fim} onChange={mudar('data_fim')} />
                </Box>
                <Box>
                    <Text fontSize="xs" color="gray.500" mb={1}>Desfecho</Text>
                    <Select size="sm" value={filtros.desfecho} onChange={mudar('desfecho')}>
                        <option value="">todos</option>
                        {Object.entries(DESFECHOS).map(([v, d]) => (
                            <option key={v} value={v}>{d.label}</option>
                        ))}
                    </Select>
                </Box>
                <Box>
                    <Text fontSize="xs" color="gray.500" mb={1}>Digitou</Text>
                    <Select size="sm" value={filtros.digito} onChange={mudar('digito')}>
                        <option value="">qualquer</option>
                        {['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                            <option key={d} value={d}>{d}</option>
                        ))}
                    </Select>
                </Box>
            </SimpleGrid>

            <Flex mt={3} gap={3} align={{ base: 'stretch', md: 'flex-end' }} direction={{ base: 'column', md: 'row' }}>
                <Box flex="1">
                    <Text fontSize="xs" color="gray.500" mb={1}>Número ou cliente</Text>
                    <Input
                        size="sm" placeholder="11 98888-7777 ou nome do cliente"
                        value={filtros.q} onChange={mudar('q')}
                        onKeyDown={(e) => { if (e.key === 'Enter') onBuscar(); }}
                    />
                </Box>
                <Checkbox size="sm" colorScheme="orange" isChecked={filtros.com_gravacao} onChange={mudar('com_gravacao')} pb={1}>
                    <Text fontSize="sm">só com gravação</Text>
                </Checkbox>
                <Button size="sm" colorScheme="orange" leftIcon={<FaSearch />} onClick={onBuscar} isLoading={carregando}>
                    Buscar
                </Button>
            </Flex>
        </Box>
    );
};

/* ==================================================================
 * Lista
 * ================================================================== */

/**
 * O desfecho pode vir deduzido dos passos (a ligação ainda não fechou, ou o
 * registro final não chegou). Nesse caso a tela marca como provisório em vez de
 * afirmar — e um rótulo que a tela não conhece vem escrito pelo servidor.
 */
const Selo = ({ ligacao }) => {
    const d = desfechoDe(ligacao.desfecho);
    const conhecido = !!DESFECHOS[ligacao.desfecho];
    const texto = conhecido ? d.curto : (ligacao.desfecho_rotulo || d.curto);
    const provisorio = !!ligacao.desfecho_derivado;

    return (
        <Tooltip
            label="Desfecho deduzido pelos passos da ligação — ainda não é o registro final."
            isDisabled={!provisorio}
        >
            <Badge colorScheme={d.cor} fontSize="10px" variant={provisorio ? 'outline' : 'subtle'}>
                {texto}{provisorio ? ' *' : ''}
            </Badge>
        </Tooltip>
    );
};

const Marcas = ({ ligacao }) => (
    <HStack spacing={1}>
        {ligacao.tem_gravacao && (
            <Tooltip label="tem gravação"><Box as={FaMicrophone} color="gray.400" boxSize="11px" /></Tooltip>
        )}
        {ligacao.tem_resumo && (
            <Tooltip label="tem resumo da IA"><Box as={FaFileAlt} color="gray.400" boxSize="11px" /></Tooltip>
        )}
    </HStack>
);

const LinhaTabela = ({ ligacao, onAbrir }) => (
    <Tr _hover={{ bg: 'orange.50' }} cursor="pointer" onClick={() => onAbrir(ligacao.id)}>
        <Td fontSize="xs" whiteSpace="nowrap">{fmtDataHora(ligacao.iniciada_em)}</Td>
        <Td fontSize="xs">
            <Text fontWeight="semibold">{fmtNumero(ligacao.numero)}</Text>
            {ligacao.cliente && <Text color="gray.500" noOfLines={1}>{ligacao.cliente.nome}</Text>}
        </Td>
        <Td textAlign="center">
            {ligacao.digito
                ? <Badge colorScheme="orange" fontSize="10px">{ligacao.digito}</Badge>
                : <Text fontSize="xs" color="gray.400">—</Text>}
        </Td>
        <Td><Selo ligacao={ligacao} /></Td>
        <Td fontSize="xs" color="gray.600" maxW="180px">
            <Text noOfLines={1}>{ligacao.no_final_nome || '—'}</Text>
        </Td>
        <Td fontSize="xs" whiteSpace="nowrap">{ligacao.atendido_por || '—'}</Td>
        <Td fontSize="xs" textAlign="right" whiteSpace="nowrap">{fmtSeg(ligacao.duracao_seg)}</Td>
        <Td><Marcas ligacao={ligacao} /></Td>
        <Td><Box as={FaChevronRight} color="gray.300" boxSize="11px" /></Td>
    </Tr>
);

const CartaoMobile = ({ ligacao, onAbrir }) => (
    <Box
        bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" p={3}
        onClick={() => onAbrir(ligacao.id)} cursor="pointer" _active={{ bg: 'orange.50' }}
    >
        <Flex justify="space-between" align="flex-start" gap={2}>
            <Box minW={0}>
                <Text fontSize="sm" fontWeight="bold" color="gray.700">{fmtNumero(ligacao.numero)}</Text>
                {ligacao.cliente && <Text fontSize="xs" color="gray.500" noOfLines={1}>{ligacao.cliente.nome}</Text>}
            </Box>
            <Selo ligacao={ligacao} />
        </Flex>

        <Wrap spacing={2} mt={2}>
            <WrapItem><Tag size="sm" variant="subtle"><TagLabel fontSize="10px">{fmtDataHora(ligacao.iniciada_em)}</TagLabel></Tag></WrapItem>
            {ligacao.digito && (
                <WrapItem><Tag size="sm" colorScheme="orange"><TagLabel fontSize="10px">digitou {ligacao.digito}</TagLabel></Tag></WrapItem>
            )}
            <WrapItem><Tag size="sm" variant="subtle"><TagLabel fontSize="10px">{fmtSeg(ligacao.duracao_seg)}</TagLabel></Tag></WrapItem>
            {ligacao.atendido_por && (
                <WrapItem><Tag size="sm" variant="subtle"><TagLabel fontSize="10px">{ligacao.atendido_por}</TagLabel></Tag></WrapItem>
            )}
        </Wrap>

        <Flex justify="space-between" align="center" mt={2}>
            <Text fontSize="10px" color="gray.400" noOfLines={1}>parou em: {ligacao.no_final_nome || '—'}</Text>
            <Marcas ligacao={ligacao} />
        </Flex>
    </Box>
);

/* ==================================================================
 * Ficha: o caminho, a gravação e o que foi dito
 * ================================================================== */

const Passo = ({ passo, ultimo }) => {
    const v = visualDoNo(passo.no_tipo);
    const condicao = passo.no_tipo === 'condicao' ? (ROTULO_CONDICAO[passo.detalhe] || passo.detalhe) : null;

    return (
        <Flex gap={3} align="stretch">
            {/* trilho */}
            <Flex direction="column" align="center" flexShrink={0}>
                <Flex
                    w="28px" h="28px" borderRadius="full" align="center" justify="center"
                    bg={`${v.cor}.50`} border="2px solid" borderColor={`${v.cor}.200`}
                >
                    <Box as={v.icone} color={`${v.cor}.500`} boxSize="12px" />
                </Flex>
                {!ultimo && <Box flex="1" w="2px" bg="gray.200" minH="14px" />}
            </Flex>

            <Box pb={ultimo ? 0 : 4} minW={0} flex="1">
                <Flex align="center" gap={2} wrap="wrap">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">{passo.no_nome}</Text>
                    <Badge fontSize="9px" colorScheme={v.cor} variant="subtle">{v.rotulo}</Badge>
                    {passo.duracao_ms != null && (
                        <Text fontSize="10px" color="gray.400">{fmtMs(passo.duracao_ms)}</Text>
                    )}
                </Flex>

                <Wrap spacing={1} mt={1}>
                    {passo.evento && (
                        <WrapItem>
                            <Tag size="sm" variant="subtle" colorScheme="gray">
                                <TagLabel fontSize="10px">
                                    {passo.evento === 'digito' && passo.digito
                                        ? `digitou ${passo.digito}`
                                        : rotuloSaida(passo.evento)}
                                </TagLabel>
                            </Tag>
                        </WrapItem>
                    )}
                    {condicao && (
                        <WrapItem><Tag size="sm" colorScheme="blue" variant="subtle"><TagLabel fontSize="10px">{condicao}</TagLabel></Tag></WrapItem>
                    )}
                    {passo.resultado && (
                        <WrapItem>
                            <Tag size="sm" colorScheme={passo.resultado === 'atendeu' ? 'green' : 'gray'} variant="subtle">
                                <TagLabel fontSize="10px">{rotuloSaida(passo.resultado)}</TagLabel>
                            </Tag>
                        </WrapItem>
                    )}
                    {passo.detalhe && !condicao && (
                        <WrapItem><Tag size="sm" variant="subtle"><TagLabel fontSize="10px">{passo.detalhe}</TagLabel></Tag></WrapItem>
                    )}
                </Wrap>

                <Text fontSize="10px" color="gray.400" mt={1}>
                    {fmtHoraMin(passo.criado_em)}
                    {passo.offset_ms ? ` · ${fmtMs(passo.offset_ms)} depois do começo` : ''}
                </Text>
            </Box>
        </Flex>
    );
};

const Dialogo = ({ linhas }) => (
    <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3} maxH="320px" overflowY="auto">
        <VStack align="stretch" spacing={2}>
            {linhas.map((d, i) => {
                const nosso = d.speaker === 'atendente';
                return (
                    <Flex key={i} justify={nosso ? 'flex-end' : 'flex-start'}>
                        <Box
                            maxW="82%" px={3} py={2} borderRadius="lg" boxShadow="sm"
                            bg={nosso ? 'blue.500' : 'white'} color={nosso ? 'white' : 'gray.800'}
                            border={nosso ? 'none' : '1px solid'} borderColor="gray.200"
                        >
                            <Text fontSize="9px" fontWeight="bold" textTransform="uppercase" opacity={0.8} mb={0.5}>
                                {nosso ? 'Atendente' : 'Cliente'}
                            </Text>
                            <Text fontSize="sm">{d.texto}</Text>
                        </Box>
                    </Flex>
                );
            })}
        </VStack>
    </Box>
);

/** O log do atendente virtual vem em pedaços de palavra: junta quem fala seguido. */
const juntarFalas = (dialogo) => {
    if (!Array.isArray(dialogo)) return [];
    const out = [];
    dialogo.forEach((d) => {
        const quem = d.quem === 'ia' ? 'atendente' : 'cliente';
        const ultimo = out[out.length - 1];
        if (ultimo && ultimo.speaker === quem) {
            ultimo.texto = `${ultimo.texto} ${d.texto || ''}`.trim();
        } else {
            out.push({ speaker: quem, texto: (d.texto || '').trim() });
        }
    });
    return out.filter((d) => d.texto);
};

const Secao = ({ titulo, children, acao }) => (
    <Box>
        <Flex align="center" justify="space-between" mb={2} gap={2}>
            <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wide">{titulo}</Text>
            {acao}
        </Flex>
        {children}
    </Box>
);

const Numero = ({ rotulo, valor }) => (
    <Box>
        <Text fontSize="10px" color="gray.500" textTransform="uppercase">{rotulo}</Text>
        <Text fontSize="sm" fontWeight="semibold" color="gray.700">{valor}</Text>
    </Box>
);

const Ficha = ({ aberta, onFechar, ficha, carregando, audioUrl, carregandoAudio, onOuvir }) => {
    const c = ficha?.chamada;
    const d = c ? desfechoDe(c.desfecho) : null;
    const falasBot = juntarFalas(ficha?.bot?.dialogo);

    return (
        <Drawer isOpen={aberta} onClose={onFechar} size={{ base: 'full', md: 'lg' }} placement="right">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px" py={3}>
                    {carregando || !c ? (
                        <Text fontSize="md">Ligação</Text>
                    ) : (
                        <Box pr={6}>
                            <HStack spacing={2} wrap="wrap">
                                <Box as={FaPhoneAlt} color={ACCENT} boxSize="13px" />
                                <Text fontSize="md">{fmtNumero(c.numero)}</Text>
                                <Badge colorScheme={d.cor}>{d.label}</Badge>
                                {c.digito && <Badge colorScheme="orange">digitou {c.digito}</Badge>}
                            </HStack>
                            <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
                                {fmtDataHora(c.iniciada_em)}
                                {ficha.cliente ? ` · ${ficha.cliente.nome}` : ''}
                                {c.versao ? ` · fluxo v${c.versao}` : ''}
                            </Text>
                        </Box>
                    )}
                </DrawerHeader>

                <DrawerBody px={{ base: 3, md: 5 }} py={4}>
                    {carregando || !c ? (
                        <Flex justify="center" py={12}><Spinner color={ACCENT} /></Flex>
                    ) : (
                        <VStack align="stretch" spacing={5}>
                            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
                                <Numero rotulo="Esperou" valor={fmtSeg(c.espera_seg)} />
                                <Numero rotulo="Conversa" valor={fmtSeg(c.duracao_seg)} />
                                <Numero rotulo="Atendeu" valor={c.atendido_por || '—'} />
                                <Numero rotulo="Status" valor={c.status || '—'} />
                            </SimpleGrid>

                            <Divider />

                            <Secao titulo="Gravação">
                                {ficha.gravacao?.disponivel ? (
                                    audioUrl ? (
                                        <Box as="audio" controls autoPlay src={audioUrl} w="100%" />
                                    ) : (
                                        // Sob demanda: o áudio de uma ligação longa são
                                        // megabytes, e quem abre a ficha muitas vezes só
                                        // quer ver o caminho. No celular, na rua, isso pesa.
                                        <Button
                                            size="sm" colorScheme="orange" variant="outline"
                                            leftIcon={<FaMicrophone />} onClick={onOuvir}
                                            isLoading={carregandoAudio} loadingText="baixando o áudio"
                                        >
                                            Ouvir a gravação
                                        </Button>
                                    )
                                ) : (
                                    <Text fontSize="xs" color="gray.500">
                                        {ficha.gravacao?.restrita || ficha.gravacao?.status === 'restrita'
                                            ? 'A gravação existe, mas é de quem atendeu a ligação. Só o atendente e o administrador podem ouvir.'
                                            : ficha.gravacao?.status === 'pendente'
                                                ? 'A gravação ainda está sendo processada.'
                                                : 'Esta ligação não tem gravação — ninguém chegou a atender.'}
                                    </Text>
                                )}
                            </Secao>

                            <Secao titulo={`Caminho na URA (${ficha.caminho?.length || 0} passos)`}>
                                {ficha.caminho?.length ? (
                                    <VStack align="stretch" spacing={0}>
                                        {ficha.caminho.map((p, i) => (
                                            <Passo key={p.seq} passo={p} ultimo={i === ficha.caminho.length - 1} />
                                        ))}
                                    </VStack>
                                ) : (
                                    <Text fontSize="xs" color="gray.500">
                                        Esta ligação não passou pelo fluxo — ou é anterior ao histórico.
                                    </Text>
                                )}
                            </Secao>

                            {/* `restrita` = existe conteúdo, mas é do atendente que
                                falou com o cliente (dono ou admin, como no /discador).
                                Seção vazia sem explicação parecia defeito. */}
                            {(ficha.resumo?.texto || ficha.resumo?.restrita) && (
                                <Secao titulo="Resumo da conversa (IA)">
                                    {ficha.resumo?.restrita ? (
                                        <Text fontSize="xs" color="gray.500">
                                            Esta ligação tem resumo, mas ele é de quem atendeu. Só o atendente e o administrador podem ver.
                                        </Text>
                                    ) : (
                                        <Box bg="orange.50" border="1px solid" borderColor="orange.100" borderRadius="md" p={3}>
                                            <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">{ficha.resumo.texto}</Text>
                                        </Box>
                                    )}
                                </Secao>
                            )}

                            {(ficha.transcricao?.dialogo?.length > 0 || ficha.transcricao?.texto || ficha.transcricao?.restrita) && (
                                <Secao titulo="Transcrição">
                                    {ficha.transcricao?.restrita ? (
                                        <Text fontSize="xs" color="gray.500">
                                            Esta ligação tem transcrição, mas ela é de quem atendeu. Só o atendente e o administrador podem ver.
                                        </Text>
                                    ) : ficha.transcricao.dialogo?.length > 0 ? (
                                        <Dialogo linhas={ficha.transcricao.dialogo} />
                                    ) : (
                                        <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3} maxH="260px" overflowY="auto">
                                            <Text fontSize="sm" whiteSpace="pre-wrap">{ficha.transcricao.texto}</Text>
                                        </Box>
                                    )}
                                </Secao>
                            )}

                            {ficha.bot && (
                                <Secao titulo="Atendente virtual">
                                    <Wrap spacing={2} mb={2}>
                                        <WrapItem><Tag size="sm"><TagLabel>{ficha.bot.status || '—'}</TagLabel></Tag></WrapItem>
                                        <WrapItem><Tag size="sm"><TagLabel>{fmtSeg(ficha.bot.duracao_seg)}</TagLabel></Tag></WrapItem>
                                        {ficha.bot.transferida_para && (
                                            <WrapItem><Tag size="sm" colorScheme="green"><TagLabel>transferiu: {ficha.bot.transferida_para}</TagLabel></Tag></WrapItem>
                                        )}
                                        {ficha.bot.motivo_encerramento && (
                                            <WrapItem><Tag size="sm"><TagLabel>{ficha.bot.motivo_encerramento}</TagLabel></Tag></WrapItem>
                                        )}
                                    </Wrap>
                                    {falasBot.length > 0 ? (
                                        <Dialogo linhas={falasBot} />
                                    ) : ficha.bot.transcricao ? (
                                        <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3} maxH="260px" overflowY="auto">
                                            <Text fontSize="sm" whiteSpace="pre-wrap">{ficha.bot.transcricao}</Text>
                                        </Box>
                                    ) : (
                                        <Text fontSize="xs" color="gray.500">Sem transcrição da conversa com o atendente virtual.</Text>
                                    )}
                                </Secao>
                            )}
                        </VStack>
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};

/* ==================================================================
 * A aba
 * ================================================================== */

const HistoricoTab = ({ pulo, aoLimparPulo }) => {
    const toast = useToast();
    const ehMobile = useBreakpointValue({ base: true, lg: false }, { ssr: false });

    // O formulário e o que está valendo na tela são coisas diferentes: digitar
    // no campo de busca não pode disparar uma consulta por letra.
    const [filtros, setFiltros] = useState(filtroInicial);
    const [aplicado, setAplicado] = useState(filtroInicial);
    const [ligacoes, setLigacoes] = useState([]);
    const [paginacao, setPaginacao] = useState({ atual: 1, ultima: 1, total: 0 });
    const [pagina, setPagina] = useState(1);
    const [carregando, setCarregando] = useState(true);
    // O nome da caixa é do Painel (por_no[].nome); o filtro viaja com o id.
    // Mostrar "menu_a3f9k1" na tela não diz nada a ninguém.
    const [noNome, setNoNome] = useState('');

    // O painel manda período + recorte quando se clica num número lá. Sem o
    // período junto, o 12 que a pessoa viu virava uma lista de outro tamanho.
    useEffect(() => {
        if (!pulo) return;
        // `no_nome` é só para a tela: não é filtro e não pode ir para a API.
        const { no_nome: nome, ...recorte } = pulo;
        setNoNome(nome || '');
        const novo = { ...filtroInicial(), ...recorte };
        setFiltros(novo);
        setAplicado(novo);
    }, [pulo]);

    const [aberta, setAberta] = useState(false);
    const [ficha, setFicha] = useState(null);
    const [carregandoFicha, setCarregandoFicha] = useState(false);
    const [audioUrl, setAudioUrl] = useState(null);
    const [carregandoAudio, setCarregandoAudio] = useState(false);
    const audioUrlRef = useRef(null);

    // Quem abriu por último manda. Sem isso, abrir a ligação A, mandar tocar e
    // pular para a B faz o áudio de A chegar atrasado e tocar dentro da ficha
    // de B — gravação de ligação é justamente o que não pode trocar de dono.
    const pedido = useRef(0);

    const carregar = useCallback(async (pg = 1) => {
        setCarregando(true);
        try {
            const enviar = Object.fromEntries(
                Object.entries({ ...aplicado, com_gravacao: aplicado.com_gravacao ? 1 : '' })
                    .filter(([, v]) => v !== '' && v != null && v !== false)
            );

            const { data } = await historicoUra({ ...enviar, page: pg, per_page: 25 });
            setLigacoes(data.data || []);
            setPaginacao({ atual: data.current_page || 1, ultima: data.last_page || 1, total: data.total || 0 });
            setPagina(data.current_page || 1);
        } catch (e) {
            toast({ title: 'Não consegui carregar as ligações.', status: 'error', duration: 4000, isClosable: true });
        } finally {
            setCarregando(false);
        }
    }, [aplicado, toast]);

    useEffect(() => { carregar(1); }, [carregar]);

    const soltarAudio = useCallback(() => {
        if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
        }
        setAudioUrl(null);
    }, []);

    useEffect(() => soltarAudio, [soltarAudio]);

    const ouvir = useCallback(async (id) => {
        const meu = pedido.current;
        setCarregandoAudio(true);
        const blob = await audioHistoricoUra(id);

        if (meu !== pedido.current) {
            return; // outra ligação assumiu a ficha enquanto isto baixava
        }
        setCarregandoAudio(false);

        if (!blob) {
            toast({ title: 'Não consegui carregar a gravação.', status: 'error', duration: 4000, isClosable: true });
            return;
        }

        const url = URL.createObjectURL(blob);
        audioUrlRef.current = url;
        setAudioUrl(url);
    }, [toast]);

    const abrir = async (id) => {
        const meu = ++pedido.current;
        setAberta(true);
        setCarregandoFicha(true);
        setFicha(null);
        soltarAudio();

        try {
            const { data } = await detalheHistoricoUra(id);
            if (meu !== pedido.current) {
                return;
            }
            setFicha(data.data || null);
        } catch (e) {
            if (meu !== pedido.current) {
                return;
            }
            toast({ title: 'Não consegui abrir a ligação.', status: 'error', duration: 4000, isClosable: true });
            setAberta(false);
        } finally {
            if (meu === pedido.current) {
                setCarregandoFicha(false);
            }
        }
    };

    const fechar = () => {
        pedido.current++;
        setAberta(false);
        setCarregandoAudio(false);
        soltarAudio();
    };

    return (
        <Box>
            {aplicado.no && (
                <Flex bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" px={3} py={2} mb={3} align="center" justify="space-between" gap={2}>
                    <Text fontSize="xs" color="gray.700">
                        Mostrando só quem passou pela caixa <b>{noNome || aplicado.no}</b>.
                    </Text>
                    <Button
                        size="xs" variant="ghost"
                        onClick={() => {
                            // A versão entra junto com a caixa (o drill-down do
                            // mapa é de uma versão só); sai junto também.
                            setFiltros((f) => ({ ...f, no: '', versao: '' }));
                            setAplicado((a) => ({ ...a, no: '', versao: '' }));
                            setNoNome('');
                            aoLimparPulo?.();
                        }}
                    >
                        ver todas
                    </Button>
                </Flex>
            )}

            <Filtros filtros={filtros} setFiltros={setFiltros} onBuscar={() => setAplicado({ ...filtros })} carregando={carregando} />

            <Flex justify="space-between" align="center" mb={2} px={1}>
                <Text fontSize="xs" color="gray.500">
                    {carregando ? 'carregando…' : `${paginacao.total} ligação(ões)`}
                </Text>
                <IconButton
                    aria-label="Atualizar" icon={<FaSyncAlt />} variant="ghost"
                    size={ehMobile ? 'sm' : 'xs'} h={ehMobile ? '40px' : undefined} minW={ehMobile ? '40px' : undefined}
                    onClick={() => carregar(pagina)}
                />
            </Flex>

            {carregando ? (
                <Flex justify="center" py={10}><Spinner color={ACCENT} /></Flex>
            ) : !ligacoes.length ? (
                <Box bg="white" border="1px dashed" borderColor="gray.300" borderRadius="lg" py={10} textAlign="center">
                    <Box as={FaRegClock} color="gray.300" boxSize="26px" mx="auto" mb={2} />
                    <Text fontSize="sm" color="gray.500">Nenhuma ligação no período.</Text>
                </Box>
            ) : ehMobile ? (
                <VStack align="stretch" spacing={2}>
                    {ligacoes.map((l) => <CartaoMobile key={l.id} ligacao={l} onAbrir={abrir} />)}
                </VStack>
            ) : (
                <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="lg" overflowX="auto">
                    <Table size="sm">
                        <Thead bg="gray.50">
                            <Tr>
                                <Th fontSize="10px">Quando</Th>
                                <Th fontSize="10px">Quem ligou</Th>
                                <Th fontSize="10px" textAlign="center">Digitou</Th>
                                <Th fontSize="10px">Desfecho</Th>
                                <Th fontSize="10px">Parou em</Th>
                                <Th fontSize="10px">Atendeu</Th>
                                <Th fontSize="10px" isNumeric>Conversa</Th>
                                <Th fontSize="10px" />
                                <Th fontSize="10px" />
                            </Tr>
                        </Thead>
                        <Tbody>
                            {ligacoes.map((l) => <LinhaTabela key={l.id} ligacao={l} onAbrir={abrir} />)}
                        </Tbody>
                    </Table>
                </Box>
            )}

            {paginacao.ultima > 1 && (
                <Flex justify="center" align="center" gap={3} mt={3}>
                    <IconButton
                        aria-label="Anterior" icon={<FaChevronLeft />} variant="outline"
                        size={ehMobile ? 'md' : 'xs'} h={ehMobile ? '44px' : undefined} minW={ehMobile ? '44px' : undefined}
                        isDisabled={paginacao.atual <= 1} onClick={() => carregar(paginacao.atual - 1)}
                    />
                    <Text fontSize="xs" color="gray.600">{paginacao.atual} de {paginacao.ultima}</Text>
                    <IconButton
                        aria-label="Próxima" icon={<FaChevronRight />} variant="outline"
                        size={ehMobile ? 'md' : 'xs'} h={ehMobile ? '44px' : undefined} minW={ehMobile ? '44px' : undefined}
                        isDisabled={paginacao.atual >= paginacao.ultima} onClick={() => carregar(paginacao.atual + 1)}
                    />
                </Flex>
            )}

            <Ficha
                aberta={aberta} onFechar={fechar} ficha={ficha} carregando={carregandoFicha}
                audioUrl={audioUrl} carregandoAudio={carregandoAudio}
                onOuvir={() => ficha?.chamada?.id && ouvir(ficha.chamada.id)}
            />
        </Box>
    );
};

export default HistoricoTab;
